import { Paper, Typography, Box } from "@mui/material";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { MdTrendingUp } from "react-icons/md";
import { useSubscriptionsReport } from "../../hooks/useReportDashboard";
import { ErrorRetry } from "../../components/common/ErrorRetry";

const funnelData = [
  { stage: "Free Users", count: 7351, percentage: 100, color: "#94A3B8" },
  { stage: "Trial Started", count: 1240, percentage: 16.9, color: "#60A5FA" },
  { stage: "Pro Converted", count: 850, percentage: 68.5, color: "#1F4FFF" },
  { stage: "Premium Upgraded", count: 285, percentage: 33.5, color: "#9333EA" },
];

const churnData = [
  { reason: "Price", value: 35, color: "#DC2626" },
  { reason: "Features", value: 25, color: "#F59E0B" },
  { reason: "Competition", value: 20, color: "#EF4444" },
  { reason: "Support", value: 12, color: "#F97316" },
  { reason: "Other", value: 8, color: "#94A3B8" },
];

export default function Analytics() {
  const {
    data: subscriptionReportsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useSubscriptionsReport();

  if (isError)
    return (
      <ErrorRetry
        message={error?.message || "Unexpected error"}
        onRetry={refetch}
      />
    );

  const conversionData =
    subscriptionReportsData?.data?.subscriberGrowthTrendAnnual || [];
  return (
    <Box>
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h5" className="font-bold text-slate-900">
          Analytics & Insights
        </Typography>
        <Typography variant="body2" className="text-slate-600 mt-1">
          Track user behavior, conversions, and churn patterns
        </Typography>
      </Box>

      {/* Main Conversion Chart */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          height: 450,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" className="font-semibold mb-4">
          User Conversion Trends
        </Typography>
        <Box sx={{ flex: 1, minHeight: 350, minWidth: 200 }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={200}>
              <AreaChart data={conversionData}>
                <defs>
                  <linearGradient id="colorFree" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#94A3B8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorTrial" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPro" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1F4FFF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1F4FFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="totalSubscribers"
                  stroke="#94A3B8"
                  fillOpacity={1}
                  fill="url(#colorFree)"
                  name="Total Subscribers"
                />
                <Area
                  type="monotone"
                  dataKey="new"
                  stroke="#60A5FA"
                  fillOpacity={1}
                  fill="url(#colorTrial)"
                  name="New"
                />
                <Area
                  type="monotone"
                  dataKey="churned"
                  stroke="#1F4FFF"
                  fillOpacity={1}
                  fill="url(#colorPro)"
                  name="Churned"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 3,
        }}
      >
        {/* Conversion Funnel */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            height: 450,
          }}
        >
          <Typography variant="h6" className="font-semibold mb-4">
            Conversion Funnel
          </Typography>
          <Box className="space-y-4">
            {funnelData.map((stage, index) => (
              <Box key={index}>
                <Box className="flex justify-between items-center mb-2">
                  <Typography className="font-semibold text-slate-900">
                    {stage.stage}
                  </Typography>
                  <Box className="flex items-center gap-2">
                    <Typography className="text-slate-900 font-bold">
                      {stage.count.toLocaleString()}
                    </Typography>
                    {index > 0 && (
                      <Box className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-lg">
                        <MdTrendingUp className="text-green-600" size={14} />
                        <Typography className="text-green-600 text-xs font-semibold">
                          {stage.percentage}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: 48,
                    bgcolor: "#F8FAFC",
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      width: index === 0 ? "100%" : `${stage.percentage}%`,
                      height: "100%",
                      bgcolor: stage.color,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "width 0.5s ease",
                    }}
                  >
                    <Typography className="text-white font-semibold">
                      {index === 0 ? "100%" : `${stage.percentage}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Churn Breakdown */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            height: 450,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" className="font-semibold mb-4">
            Churn Breakdown
          </Typography>
          <Box sx={{ flex: 1, minHeight: 250, minWidth: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200}>
              <BarChart data={churnData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" />
                <YAxis
                  dataKey="reason"
                  type="category"
                  stroke="#64748B"
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                  formatter={(value) => [`${value}%`, "Percentage"]}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {churnData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box className="mt-4 space-y-2">
            {churnData.map((item, index) => (
              <Box key={index} className="flex items-center justify-between">
                <Box className="flex items-center gap-2">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "3px",
                      bgcolor: item.color,
                    }}
                  />
                  <Typography variant="body2" className="text-slate-700">
                    {item.reason}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  className="font-semibold text-slate-900"
                >
                  {item.value}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
